import { useEffect, useRef, type RefObject } from 'react';
import { analytics, newId, type SemanticContext, type TrackDisposition } from '../client';
import {
  beginAction,
  beginWorkflow,
  capturePageExecution,
  createSearchContext,
  currentAccountScope,
  getPageView,
  immutableContext,
  reportContextDiagnostic,
  sameAccount,
  type AccountScope,
  type BrowserActionContext,
  type BrowserSearchContext,
  type RequestAnalyticsContext,
} from '../context';
import type {
  AnalyticsExecutionSnapshot,
  AnalyticsSurface,
  AttributionContext,
  CountStatus,
  CriteriaSnapshot,
  EntityReference,
  SearchTrigger,
  WorkflowType,
} from '../contract-v2';
import { executionForResponse } from '../requests';

export type SearchStartTrigger = Extract<
  SearchTrigger,
  'search_button' | 'apply_filters' | 'initial_load' | 'inherited_selection' | 'deep_link'
>;
export type PageNavigation = 'next' | 'previous' | 'jump' | 'page_size' | 'keyboard';

/** Business cache identity exists even when analytics is disabled. No global last-search slot. */
export interface AppliedSearch<Params = Record<string, unknown>> {
  readonly id: string;
  readonly params: Readonly<Params>;
  readonly criteria: CriteriaSnapshot;
  readonly surface: AnalyticsSurface;
  readonly trigger: SearchStartTrigger;
  readonly accountScope?: AccountScope;
  readonly search?: BrowserSearchContext;
  readonly action?: BrowserActionContext;
  readonly execution?: AnalyticsExecutionSnapshot;
  readonly executionKind: 'interactive' | 'automatic';
}

export interface BeginSearchInput<Params> {
  surface: AnalyticsSurface;
  params: Params;
  criteria: CriteriaSnapshot;
  trigger: SearchStartTrigger;
  context?: SemanticContext;
  action?: BrowserActionContext;
  parentEntity?: EntityReference;
  workflowType?: WorkflowType;
  execution?: AnalyticsExecutionSnapshot;
  executionKind?: 'interactive' | 'automatic';
}

/** Metadata on a Dva action, separate from its business payload. */
export interface SearchIntent {
  readonly trigger: SearchTrigger;
  readonly context?: SemanticContext;
  readonly action?: BrowserActionContext;
  readonly execution?: AnalyticsExecutionSnapshot;
  readonly executionKind?: 'interactive' | 'automatic';
  readonly navigationMethod?: PageNavigation;
  readonly applyReason?: 'apply' | 'reset';
}

const copy = <T>(value: T): T => JSON.parse(JSON.stringify(value));

/** Called only after business validation accepts a new run, including deliberate resubmission. */
export function beginSearch<Params>(input: BeginSearchInput<Params>): AppliedSearch<Params> {
  if (
    !input.trigger ||
    (input.execution && !sameAccount(input.execution as AccountScope, currentAccountScope()))
  ) {
    // Migration omissions remain usable but cannot masquerade as a deliberate submission.
    reportContextDiagnostic('missing_context');
    return immutableContext({
      id: newId(),
      params: copy(input.params),
      criteria: copy(input.criteria),
      surface: input.surface,
      trigger: input.trigger,
      accountScope: currentAccountScope(),
      executionKind: 'automatic',
    });
  }
  const executionKind =
    input.executionKind ||
    (input.trigger === 'initial_load' || input.trigger === 'deep_link'
      ? 'automatic'
      : 'interactive');
  let context = input.context || input.action || input.execution?.attribution;
  if (!context?.workflow_id && !input.action?.workflow_id && input.workflowType) {
    const workflow = beginWorkflow({
      type: input.workflowType,
      surface: input.surface,
      parentEntity: input.parentEntity,
      origin: context,
      reset: true,
    });
    if (workflow) {
      context = workflow;
      const { accountScope: _accountScope, ...properties } = workflow;
      analytics.trackSemantic('workflow_started', workflow, properties, {
        factKey: workflow.workflow_id,
        executionKind,
        ...(executionKind === 'automatic' && input.execution
          ? {
              userId: input.execution.userId,
              clientId: input.execution.clientId,
              deviceId: input.execution.deviceId,
              sessionId: input.execution.sessionId,
              accountEpoch: input.execution.accountEpoch,
            }
          : {}),
      });
    }
  }
  const action =
    input.action ||
    (executionKind === 'interactive'
      ? beginAction({ surface: input.surface, context, trigger: input.trigger })
      : undefined);
  const search = createSearchContext({
    surface: input.surface,
    context,
    action,
    parentEntity: input.parentEntity,
    criteriaVersion: input.criteria.criteria_version,
    submittedCriteria: input.criteria,
    trigger: input.trigger,
  });
  const execution =
    input.execution ||
    (executionKind === 'automatic'
      ? capturePageExecution(context || { surface: input.surface })
      : undefined);
  return immutableContext({
    id: newId(),
    params: copy(input.params),
    criteria: copy(input.criteria),
    surface: input.surface,
    trigger: input.trigger,
    accountScope: currentAccountScope(),
    search,
    action,
    execution,
    executionKind,
  });
}

export interface SearchPageRequest {
  readonly id: string;
  readonly runId: string;
  readonly page: number | null;
  readonly pageSize: number;
  readonly previousPage: number | null;
  readonly offset: number;
  readonly trigger: SearchTrigger;
  readonly navigationMethod?: PageNavigation;
  readonly analyticsContext?: RequestAnalyticsContext;
}

export interface PageRequestInput {
  page: number | null;
  pageSize: number;
  previousPage?: number | null;
  offset?: number;
  trigger: SearchTrigger;
  phase: 'submit' | 'page';
  navigationMethod?: PageNavigation;
  action?: BrowserActionContext;
  /** Required for automatic batches; never borrow the latest interactive session. */
  execution?: AnalyticsExecutionSnapshot;
}

/** Reuse the returned object (or prepared Umi config) for a transport retry. */
export function contextForPageRequest(
  run: AppliedSearch<any>,
  input: PageRequestInput,
): SearchPageRequest {
  const isSubmit = input.phase === 'submit';
  const automatic =
    input.trigger === 'automatic_batch' || (isSubmit && run.executionKind === 'automatic');
  const action =
    input.action ||
    (isSubmit ? run.action : undefined) ||
    (!automatic
      ? beginAction({ surface: run.surface, context: run.search, trigger: input.trigger })
      : undefined);
  const attribution: AttributionContext = {
    surface: run.surface,
    page_number: input.page,
    page_size: input.pageSize,
    previous_page: input.previousPage ?? null,
    offset: input.offset ?? (input.page === null ? 0 : (input.page - 1) * input.pageSize),
  };
  const operationId = newId();
  const execution = input.execution || run.execution;
  const analyticsContext: RequestAnalyticsContext | undefined =
    input.trigger && run.search && isCurrentSearch(run, run)
      ? automatic
        ? execution
          ? {
              executionKind: 'automatic',
              execution,
              search: run.search,
              action,
              attribution,
              phase: input.phase,
              trigger: input.trigger,
              operationId,
            }
          : undefined
        : action
        ? {
            executionKind: 'interactive',
            search: run.search,
            action,
            attribution,
            phase: input.phase,
            trigger: input.trigger,
            operationId,
          }
        : undefined
      : undefined;
  if (!analyticsContext) reportContextDiagnostic('missing_context');
  if (
    analyticsContext?.executionKind === 'interactive' &&
    input.navigationMethod &&
    (input.trigger === 'user_pagination' || input.trigger === 'page_size_change')
  ) {
    analytics.trackSemantic(
      'pagination_changed',
      {
        ...run.search,
        ...action,
        ...attribution,
      },
      {
        surface: run.surface,
        search_id: run.search!.search_id,
        action_id: action!.action_id,
        previous_page: input.previousPage ?? null,
        page_number: input.page,
        page_size: input.pageSize,
        navigation_method: input.navigationMethod,
      },
      { factKey: action!.action_id },
    );
  }
  return immutableContext({
    id: newId(),
    runId: run.id,
    page: input.page,
    pageSize: input.pageSize,
    previousPage: input.previousPage ?? null,
    offset: attribution.offset!,
    trigger: input.trigger,
    navigationMethod: input.navigationMethod,
    analyticsContext,
  });
}

/** Counts are execution continuations. Each logical poll has its own operation; retries reuse it. */
export function contextForDeferredCount(
  execution: AnalyticsExecutionSnapshot | undefined,
  run: AppliedSearch<any> | undefined,
): RequestAnalyticsContext | undefined {
  if (
    !execution ||
    !run?.search ||
    !isCurrentSearch(run, run) ||
    execution.searchId !== run.search.search_id
  ) {
    reportContextDiagnostic('missing_context');
    return undefined;
  }
  return immutableContext({
    executionKind: 'deferred',
    execution,
    search: run.search,
    phase: 'count',
    trigger: execution.attribution.trigger || run.trigger,
    operationId: newId(),
  });
}

export function isCurrentSearch(
  run: AppliedSearch<any> | undefined,
  current: AppliedSearch<any> | undefined,
): boolean {
  if (!run || !current || current.id !== run.id) return false;
  return !run.accountScope || sameAccount(run.accountScope, currentAccountScope());
}

export function navigationMethod(
  previousPage: number,
  page: number,
  sizeChanged = false,
): PageNavigation {
  return sizeChanged
    ? 'page_size'
    : page === previousPage + 1
    ? 'next'
    : page === previousPage - 1
    ? 'previous'
    : 'jump';
}

export interface ResultCount {
  readonly totalCount: number | null;
  readonly countStatus: CountStatus;
}

export function resultCount(total: unknown, status?: unknown): ResultCount {
  const known = typeof total === 'number' && Number.isSafeInteger(total) && total >= 0;
  if (status === 'pending') return { totalCount: null, countStatus: 'pending' };
  if (status === 'failed' || status === 'unavailable' || !known)
    return { totalCount: null, countStatus: 'unavailable' };
  return { totalCount: total as number, countStatus: 'ready' };
}

export interface ResultsPage extends ResultCount {
  readonly runId: string;
  readonly viewId: string;
  readonly context: SemanticContext;
  readonly execution?: AnalyticsExecutionSnapshot;
  readonly page: number | null;
  readonly pageSize: number;
  readonly previousPage: number | null;
  readonly offset: number;
  readonly loadSource: 'network' | 'cache';
  readonly navigationMethod?: PageNavigation;
  readonly displayStatus?: 'partial' | 'complete' | 'failed';
  readonly aggregate?: boolean;
  readonly locationCount?: number;
  readonly companyCount?: number;
}

export function resultsPageForResponse(
  run: AppliedSearch<any>,
  request: SearchPageRequest,
  response: unknown,
  facts: ResultCount &
    Pick<ResultsPage, 'displayStatus' | 'aggregate' | 'locationCount' | 'companyCount'>,
): ResultsPage {
  const execution = executionForResponse(response);
  return immutableContext({
    ...facts,
    runId: run.id,
    viewId: request.id,
    context: {
      ...run.search,
      ...request.analyticsContext?.action,
      ...execution?.attribution,
      ...request.analyticsContext?.attribution,
      surface: run.surface,
      trigger: request.trigger,
      execution_kind: request.analyticsContext?.executionKind || run.executionKind,
      ...(execution?.operationId ? { operation_id: execution.operationId } : {}),
      ...(run.accountScope ? { accountScope: run.accountScope } : {}),
    },
    execution:
      execution ||
      (request.analyticsContext?.executionKind === 'automatic'
        ? request.analyticsContext.execution
        : undefined),
    page: request.page,
    pageSize: request.pageSize,
    previousPage: request.previousPage,
    offset: request.offset,
    loadSource: 'network',
    navigationMethod: request.navigationMethod,
  });
}

/** A cache visit has a fresh view/action, while the response's applied run stays unchanged. */
export function resultsPageFromCache(page: ResultsPage, request: SearchPageRequest): ResultsPage {
  return immutableContext({
    ...page,
    viewId: request.id,
    loadSource: 'cache',
    execution: undefined,
    context: {
      ...page.context,
      ...request.analyticsContext?.action,
      ...request.analyticsContext?.attribution,
      operation_id: undefined,
      trigger: request.trigger,
      execution_kind: 'interactive',
    },
    previousPage: request.previousPage,
    page: request.page,
    pageSize: request.pageSize,
    offset: request.offset,
    navigationMethod: request.navigationMethod,
  });
}

/** Only a mounted, visible owner calls this. Fetch/model/prefetch completion is not a view. */
export function recordResultsPageDisplayed(
  page: ResultsPage,
  displayedCount: number,
): TrackDisposition | undefined {
  if (!page.context.search_id || !page.context.surface) return undefined;
  const automatic = page.context.execution_kind === 'automatic';
  if (automatic && !page.execution) {
    reportContextDiagnostic('missing_context');
    return undefined;
  }
  const execution = automatic ? page.execution : undefined;
  const currentPage = getPageView();
  const viewId = `${page.viewId}:${currentPage?.id || page.context.page_view_id || 'visible'}`;
  return analytics.trackSemantic(
    'results_page_viewed',
    {
      ...page.context,
      page_number: page.page,
      page_size: page.pageSize,
      previous_page: page.previousPage,
      offset: page.offset,
      page_view_id: currentPage?.id || page.context.page_view_id,
    },
    {
      surface: page.context.surface,
      search_id: page.context.search_id,
      view_id: viewId,
      displayed_count: displayedCount,
      total_count: page.totalCount,
      count_status: page.countStatus,
      load_source: page.loadSource,
      navigation_method: page.navigationMethod,
      display_status: page.displayStatus,
      aggregate: page.aggregate,
      location_count: page.locationCount,
      company_count: page.companyCount,
    },
    {
      factKey: viewId,
      executionKind: automatic ? 'automatic' : 'interactive',
      ...(execution
        ? {
            userId: execution.userId,
            clientId: execution.clientId,
            deviceId: execution.deviceId,
            sessionId: execution.sessionId,
            accountEpoch: execution.accountEpoch,
          }
        : {}),
    },
  );
}

function isVisible(element?: HTMLElement | null): boolean {
  if (document.visibilityState === 'hidden') return false;
  for (let node = element; node; node = node.parentElement) {
    if (node.hidden || node.getAttribute('aria-hidden') === 'true') return false;
    const style = window.getComputedStyle(node);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
  }
  return !element || element.isConnected;
}

/** Metadata changes for transitions, not for count-only updates or contact enrichment. */
export function useResultsPageDisplayed(input: {
  page?: ResultsPage;
  displayedCount: number;
  visible: boolean;
  element?: RefObject<HTMLElement>;
}): void {
  const observed = useRef<string>();
  useEffect(() => {
    const record = () => {
      // A supplied ref must be attached to the rendered result surface. During
      // route replacement the metadata may still describe the previous entity
      // while the component renders a loading/empty placeholder without that ref.
      if (
        input.page &&
        input.visible &&
        (!input.element || input.element.current) &&
        isVisible(input.element?.current)
      ) {
        const key = `${input.page.viewId}:${getPageView()?.id || ''}`;
        if (observed.current === key) return;
        const disposition = recordResultsPageDisplayed(input.page, input.displayedCount);
        if (disposition === 'queued' || disposition === 'duplicate' || disposition === 'disabled')
          observed.current = key;
      }
    };
    record();
    document.addEventListener('visibilitychange', record);
    // Retained tabs can become visible through an ancestor class/style change without
    // rerendering the result component. Observe only that ancestor chain, not row content.
    const observer =
      typeof MutationObserver === 'undefined' ? undefined : new MutationObserver(record);
    for (let node = input.element?.current; node; node = node.parentElement) {
      observer?.observe(node, {
        attributes: true,
        attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'],
      });
    }
    return () => {
      observer?.disconnect();
      document.removeEventListener('visibilitychange', record);
    };
  });
}
